import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { AdminNavigation } from './AdminNavigation';
import { db } from '../../lib/firebase';
import './admin-reports.scss';

type StepEntry = {
  name: string;
  status: string;
  timestamp: Date;
  evidence: string;
  message?: string;
};

type SOPLog = {
  id: string;
  technicianId: string;
  technicianEmail: string;
  sopName: string;
  startTime: Date;
  endTime?: Date;
  steps: StepEntry[];
  status: 'in-progress' | 'completed' | 'abandoned';
};

type AggregatedRow = { stepName: string; avgMs: number; count: number };

const parseStepOrderIndex = (stepName: string): number => {
  const match = stepName.match(/^(\d+)/);
  if (match) {
    const n = parseInt(match[1], 10);
    if (!Number.isNaN(n)) return n;
  }
  return Number.MAX_SAFE_INTEGER;
};

const formatMsToMin = (ms: number) => {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}m ${sec}s`;
};

const SimpleBarChart: React.FC<{ data: AggregatedRow[]; width?: number; height?: number }> = ({ data, width = 1000, height = 360 }) => {
  const margin = { top: 30, right: 20, bottom: 110, left: 80 };
  const innerW = Math.max(50, width - margin.left - margin.right);
  const innerH = Math.max(80, height - margin.top - margin.bottom);
  const maxValue = data.length ? Math.max(...data.map((d) => d.avgMs)) : 0;
  const barCount = data.length;
  const gap = 12;
  const barWidth = barCount > 0 ? Math.max(10, (innerW - gap * (barCount - 1)) / barCount) : 0;

  const yScale = (v: number) => {
    if (maxValue <= 0) return innerH;
    const t = v / maxValue;
    return innerH - t * innerH;
  };

  return (
    <svg width={width} height={height} className="chart-root">
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Background grid */}
        {Array.from({ length: 5 }).map((_, i) => {
          const t = i / 4;
          const val = maxValue * t;
          const y = yScale(val);
          return (
            <g key={`grid-${i}`}>
              <line x1={0} y1={y} x2={innerW} y2={y} stroke="#eee" />
              <text x={-10} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="#6b7280">
                {formatMsToMin(val)}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const x = i * (barWidth + gap);
          const y = yScale(d.avgMs);
          const h = innerH - y;
          return (
            <g key={d.stepName}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={h}
                fill="#34d399"
                rx={6}
                ry={6}
                className="chart-bar"
              >
              </rect>
              <title>{`${d.stepName}\nAvg: ${formatMsToMin(d.avgMs)}\nSamples: ${d.count}`}</title>
              <text
                x={x + barWidth / 2}
                y={innerH + 16}
                transform={`rotate(42, ${x + barWidth / 2}, ${innerH + 16})`}
                textAnchor="start"
                fontSize={11}
                fill="#374151"
              >
                {d.stepName}
              </text>
              <text
                x={x + barWidth / 2}
                y={y - 6}
                textAnchor="middle"
                fontSize={11}
                fill="#111827"
              >
                {formatMsToMin(d.avgMs)}
              </text>
            </g>
          );
        })}

        {/* Axes titles */}
        <text x={innerW / 2} y={innerH + 70} fontSize={12} fill="#374151" textAnchor="middle">Steps</text>
        <text x={-60} y={-10} fontSize={13} fill="#111827">Avg Time by Step</text>
      </g>
    </svg>
  );
};

export const AdminReports: React.FC = () => {
  const [sopLogs, setSopLogs] = useState<SOPLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSop, setSelectedSop] = useState<string>('');
  const [completedOnly, setCompletedOnly] = useState<boolean>(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const sopQuery = query(collection(db, 'sopLogs'), orderBy('startTime', 'desc'));
        const sopSnapshot = await getDocs(sopQuery);
        const logs: SOPLog[] = [];
        sopSnapshot.forEach((doc) => {
          const data: any = doc.data();
          logs.push({
            id: doc.id,
            technicianId: data.technicianId,
            technicianEmail: data.technicianEmail,
            sopName: data.sopName,
            startTime: data.startTime?.toDate?.() || new Date(data.startTime),
            endTime: data.endTime?.toDate?.() || (data.endTime ? new Date(data.endTime) : undefined),
            status: data.status,
            steps: (data.steps || []).map((s: any) => ({
              name: s.name,
              status: s.status,
              evidence: s.evidence,
              message: s.message,
              timestamp: s.timestamp?.toDate?.() || new Date(s.timestamp)
            }))
          });
        });
        setSopLogs(logs);
      } catch (e) {
        console.error('Error loading SOP logs for reports', e);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  const sopNames = useMemo(() => Array.from(new Set((sopLogs || []).map((l) => l.sopName))).sort(), [sopLogs]);

  useEffect(() => {
    if (!selectedSop && sopNames.length > 0) {
      setSelectedSop(sopNames[0]);
    }
  }, [sopNames, selectedSop]);

  const aggregatedStepDurations: AggregatedRow[] = useMemo(() => {
    if (!selectedSop) return [];
    const relevant = (sopLogs || []).filter((l) => l.sopName === selectedSop && (!completedOnly || l.status === 'completed'));
    if (relevant.length === 0) return [];

    const map = new Map<string, { totalMs: number; count: number }>();
    for (const log of relevant) {
      const stepsSorted = (log.steps || [])
        .slice()
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

      for (let i = 0; i < stepsSorted.length; i++) {
        const current = stepsSorted[i];
        const prevTime = i === 0 ? log.startTime : stepsSorted[i - 1].timestamp;
        const curTime = current.timestamp;
        if (!prevTime || !curTime) continue;
        const delta = curTime.getTime() - prevTime.getTime();
        const durationMs = Math.max(0, delta);
        if (!Number.isFinite(durationMs)) continue;

        const key = current.name || `Step ${i + 1}`;
        const agg = map.get(key) || { totalMs: 0, count: 0 };
        agg.totalMs += durationMs;
        agg.count += 1;
        map.set(key, agg);
      }
    }

    const rows: AggregatedRow[] = Array.from(map.entries()).map(([stepName, v]) => ({
      stepName,
      avgMs: v.totalMs / Math.max(1, v.count),
      count: v.count,
    }));

    rows.sort((a, b) => {
      const ai = parseStepOrderIndex(a.stepName);
      const bi = parseStepOrderIndex(b.stepName);
      if (ai !== bi) return ai - bi;
      return a.stepName.localeCompare(b.stepName);
    });

    return rows;
  }, [sopLogs, selectedSop, completedOnly]);

  return (
    <div className="admin-reports">
      <AdminNavigation />
      <header className="reports-header">
        <div>
          <h1>Reports</h1>
          <p className="subtitle">Insights across SOP executions</p>
        </div>
      </header>

      <div className="reports-card">
        <div className="card-header">
          <h2>Average Step Duration by SOP</h2>
          <div className="filters">
            <label>
              <span>SOP</span>
              <select value={selectedSop} onChange={(e) => setSelectedSop(e.target.value)}>
                {sopNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </label>
            <label className="checkbox">
              <input type="checkbox" checked={completedOnly} onChange={(e) => setCompletedOnly(e.target.checked)} />
              <span>Completed runs only</span>
            </label>
          </div>
        </div>

        <div className="chart-wrapper">
          {loading ? (
            <div className="loading">Loading data…</div>
          ) : selectedSop && aggregatedStepDurations.length > 0 ? (
            <SimpleBarChart data={aggregatedStepDurations} />
          ) : (
            <div className="empty">No data available for the current filters.</div>
          )}
        </div>
      </div>
    </div>
  );
};

